import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close, OpenInFull } from '@mui/icons-material';
import { DiveSite, DiveSiteMatch } from '../types/DiveSite';
import { DiveSitePreferencesState } from '../hooks/useDiveSitePreferences';
import 'leaflet/dist/leaflet.css';

interface DiveSiteMapProps {
  sites: DiveSite[];
  siteMatches: Map<string, DiveSiteMatch>;
  preferences: DiveSitePreferencesState;
  height?: number;
  dialogTitle?: string;
}

const FitBounds: React.FC<{ sites: DiveSite[] }> = ({ sites }) => {
  const map = useMap();
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!map || !map.getContainer()) {
      return;
    }

    const timer = setTimeout(() => {
      setIsInitialized(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [map]);

  useEffect(() => {
    if (!isInitialized || !map || !map.getContainer() || sites.length === 0) {
      return;
    }

    try {
      if (sites.length === 1) {
        const site = sites[0];
        map.setView([site.latitude, site.longitude], 7, { animate: false });
        return;
      }

      const bounds = L.latLngBounds(sites.map((site) => [site.latitude, site.longitude]));
      map.fitBounds(bounds, { padding: [24, 24], animate: false });
    } catch (error) {
      console.error('Error fitting dive site map bounds:', error);
    }
  }, [sites, map, isInitialized]);

  return null;
};

const createSiteIcon = (site: DiveSite, match: DiveSiteMatch | undefined, preferences: DiveSitePreferencesState): L.DivIcon => {
  const isFavorite = preferences.favoriteSiteIds.includes(site.id);
  const isWishlisted = preferences.wishlistSiteIds.includes(site.id);

  const color = isFavorite
    ? '#f59e0b'
    : match
      ? '#2e7d32'
      : isWishlisted
        ? '#1565c0'
        : '#00ACC1';

  const ringColor = site.featured ? '#0f172a' : '#ffffff';

  const svgIcon = `
    <svg width="28" height="44" viewBox="0 0 28 44" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.3 0 0 6.3 0 14C0 24.4 14 44 14 44S28 24.4 28 14C28 6.3 21.7 0 14 0Z" fill="${color}" stroke="${ringColor}" stroke-width="2"/>
      <circle cx="14" cy="14" r="5" fill="white"/>
    </svg>
  `;

  return new L.DivIcon({
    html: svgIcon,
    className: 'custom-dive-site-map-marker',
    iconSize: [28, 44],
    iconAnchor: [14, 44],
    popupAnchor: [0, -40],
  } as L.DivIconOptions);
};

interface RenderedMapProps {
  sites: DiveSite[];
  siteMatches: Map<string, DiveSiteMatch>;
  preferences: DiveSitePreferencesState;
  height: number;
  mapKey: number;
}

const RenderedMap: React.FC<RenderedMapProps> = ({ sites, siteMatches, preferences, height, mapKey }) => {
  return (
    <MapContainer
      key={mapKey}
      style={{ height: `${height}px`, width: '100%' }}
      zoom={2}
      center={[sites[0].latitude, sites[0].longitude]}
      zoomControl={true}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <FitBounds sites={sites} />

      {sites.map((site) => {
        const match = siteMatches.get(site.id);

        return (
          <Marker
            key={site.id}
            position={[site.latitude, site.longitude]}
            icon={createSiteIcon(site, match, preferences)}
          >
            <Popup>
              <Box sx={{ minWidth: { xs: 'min(220px, 70vw)', sm: 220 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {site.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {site.region}, {site.country}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                  {match ? <Chip size="small" color="success" label={`Dove ${match.diveCount}x`} /> : null}
                  {preferences.favoriteSiteIds.includes(site.id) ? <Chip size="small" color="warning" label="Favorite" /> : null}
                  {preferences.wishlistSiteIds.includes(site.id) ? <Chip size="small" color="primary" variant="outlined" label="Want to dive" /> : null}
                </Stack>
                <Typography variant="body2" sx={{ mb: 1.25 }}>
                  {site.summary}
                </Typography>
                <Button component={RouterLink} to={`/dive-sites/${site.slug}`} size="small" variant="outlined">
                  View details
                </Button>
              </Box>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

const DiveSiteMap: React.FC<DiveSiteMapProps> = ({ sites, siteMatches, preferences, height = 360, dialogTitle = 'Dive site map' }) => {
  const [mapKey, setMapKey] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    setMapKey((currentValue) => currentValue + 1);
  }, [sites.length]);

  if (sites.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No sites to map
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adjust the current filters or search to show more catalog points.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2, position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1000 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInFull />}
            onClick={() => setIsExpanded(true)}
          >
            Pop out map
          </Button>
        </Box>
        <RenderedMap
          sites={sites}
          siteMatches={siteMatches}
          preferences={preferences}
          height={height}
          mapKey={mapKey}
        />
      </Paper>

      <Dialog
        open={isExpanded}
        onClose={() => setIsExpanded(false)}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle sx={{ pr: 7 }}>
          {dialogTitle}
          <IconButton
            aria-label="Close expanded map"
            onClick={() => setIsExpanded(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <RenderedMap
            sites={sites}
            siteMatches={siteMatches}
            preferences={preferences}
            height={fullScreen ? 640 : 760}
            mapKey={mapKey + 1000}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DiveSiteMap;